import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AssignTenantModal = ({ isOpen, onClose, unit, existingTenants, onAssign }) => {
  const [mode, setMode] = useState('existing'); // 'existing' or 'new'
  const [selectedTenant, setSelectedTenant] = useState('');
  const [seatsToAllocate, setSeatsToAllocate] = useState(unit?.capacitySeats || 0);
  const [newTenant, setNewTenant] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    lockinMonths: 12
  });
  const [errors, setErrors] = useState({});

  if (!isOpen || !unit) return null;

  const tenantOptions = existingTenants?.map(tenant => ({
    value: tenant?.id,
    label: `${tenant?.name} (${tenant?.company})`,
    description: `Current seats: ${tenant?.seatsTaken}`
  }));

  // Calculate revenue based on pricing model
  const calculateRevenue = () => {
    switch (unit?.pricingModel) {
      case 'per_seat':
        return seatsToAllocate * (unit?.pricePerSeat || 0);
      case 'per_sqft':
        return (unit?.capacitySqft || unit?.areaSqft || 0) * (unit?.pricePerSqft || 0);
      case 'fixed':
        return unit?.fixedMonthlyPrice || 0;
      default:
        return 0;
    }
  };

  // Get pricing display label
  const getPricingLabel = () => {
    switch (unit?.pricingModel) {
      case 'per_seat':
        return `₹${unit?.pricePerSeat?.toLocaleString('en-IN')}/seat`;
      case 'per_sqft':
        return `₹${unit?.pricePerSqft?.toLocaleString('en-IN')}/sqft`;
      case 'fixed':
        return 'Fixed Monthly Price';
      default:
        return '—';
    }
  };

  // Get capacity display
  const getCapacityDisplay = () => {
    switch (unit?.pricingModel) {
      case 'per_seat':
        return `${unit?.capacitySeats} seats`;
      case 'per_sqft':
        return `${unit?.capacitySqft || unit?.areaSqft} sqft`;
      case 'fixed':
        return `${unit?.capacitySqft || unit?.areaSqft} sqft`;
      default:
        return '—';
    }
  };

  const getUnitTypeLabel = (type, coworkingSeatType) => {
    const coworkingSeatLabels = {
      hot_desk: 'Hot Desk',
      fixed_desk: 'Fixed Desk',
      manager_cabin: 'Manager Cabin',
      team_suite_with_manager: 'Team Suite (w/ Manager)',
      team_suite_without_manager: 'Team Suite'
    };

    if (type === 'coworking' && coworkingSeatType) {
      return coworkingSeatLabels?.[coworkingSeatType] || 'Coworking';
    }

    const labels = {
      coworking: 'Coworking',
      bareshell: 'Bare Shell',
      managed_office: 'Managed Office',
      furnished: 'Furnished'
    };

    return labels?.[type] || type;
  };

  const validateForm = () => {
    const newErrors = {};

    if (mode === 'existing' && !selectedTenant) {
      newErrors.tenant = 'Please select a tenant';
    }

    if (mode === 'new') {
      if (!newTenant?.name?.trim()) newErrors.name = 'Name is required';
      if (!newTenant?.email?.trim()) newErrors.email = 'Email is required';
      if (!newTenant?.company?.trim()) newErrors.company = 'Company is required';
    }

    // Validate seats only for per_seat pricing model
    if (unit?.pricingModel === 'per_seat') {
      if (seatsToAllocate < 1) {
        newErrors.seats = 'Must allocate at least 1 seat';
      }
      if (seatsToAllocate > unit?.capacitySeats) {
        newErrors.seats = `Cannot exceed ${unit?.capacitySeats} seats`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const assignmentData = {
      unitId: unit?.id,
      seatsAllocated: unit?.pricingModel === 'per_seat' ? seatsToAllocate : null,
      mode,
      ...(mode === 'existing' 
        ? { tenantId: selectedTenant }
        : { newTenant }
      )
    };

    onAssign(assignmentData);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background bg-opacity-80">
      <div className="bg-card rounded-lg shadow-brand-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
              Assign Tenant
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Unit {unit?.unitNumber} • Floor {unit?.floor} • {getUnitTypeLabel(unit?.unitType, unit?.coworkingSeatType)} • {getCapacityDisplay()}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-smooth"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-4 md:p-6 space-y-6">
          {/* Mode Selection */}
          <div className="flex gap-2">
            <Button
              variant={mode === 'existing' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMode('existing')}
              fullWidth
            >
              Existing Tenant
            </Button>
            <Button
              variant={mode === 'new' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMode('new')}
              fullWidth
            >
              New Tenant
            </Button>
          </div>

          {/* Existing Tenant Selection */}
          {mode === 'existing' && (
            <Select
              label="Select Tenant"
              description="Choose from existing tenants in the system"
              options={tenantOptions}
              value={selectedTenant}
              onChange={setSelectedTenant}
              error={errors?.tenant}
              searchable
              required
            />
          )}

          {/* New Tenant Form */}
          {mode === 'new' && (
            <div className="space-y-4">
              <Input
                label="Tenant Name"
                type="text"
                placeholder="John Doe"
                value={newTenant?.name}
                onChange={(e) => setNewTenant({ ...newTenant, name: e?.target?.value })}
                error={errors?.name}
                required
              />

              <Input
                label="Email Address"
                type="email"
                placeholder="john@company.com"
                value={newTenant?.email}
                onChange={(e) => setNewTenant({ ...newTenant, email: e?.target?.value })}
                error={errors?.email}
                required
              />

              <Input
                label="Phone Number"
                type="tel"
                placeholder="+91 98765 43210"
                value={newTenant?.phone}
                onChange={(e) => setNewTenant({ ...newTenant, phone: e?.target?.value })}
              />

              <Input
                label="Company Name"
                type="text"
                placeholder="Acme Corporation"
                value={newTenant?.company}
                onChange={(e) => setNewTenant({ ...newTenant, company: e?.target?.value })}
                error={errors?.company}
                required
              />

              <Input
                label="Lock-in Period (Months)"
                type="number"
                min="1"
                max="36"
                value={newTenant?.lockinMonths}
                onChange={(e) => setNewTenant({ ...newTenant, lockinMonths: parseInt(e?.target?.value) })}
                description="Standard lock-in period for the lease agreement"
              />
            </div>
          )}

          {/* Pricing Information */}
          <div className="bg-muted bg-opacity-30 rounded-lg p-4 space-y-4">
            <h3 className="text-base font-semibold text-foreground">Pricing Details</h3>
            
            {/* Show seat allocation only for per_seat pricing */}
            {unit?.pricingModel === 'per_seat' && (
              <Input
                label="Seats to Allocate"
                type="number"
                min="1"
                max={unit?.capacitySeats}
                value={seatsToAllocate}
                onChange={(e) => setSeatsToAllocate(parseInt(e?.target?.value) || 0)}
                error={errors?.seats}
                description={`Available seats in this unit: ${unit?.capacitySeats}`}
                required
              />
            )}

            {/* Display pricing model info */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Pricing Model</span>
                <span className="font-medium text-foreground">
                  {unit?.pricingModel === 'per_seat' && 'Per Seat'}
                  {unit?.pricingModel === 'per_sqft' && 'Per Sqft'}
                  {unit?.pricingModel === 'fixed' && 'Fixed Monthly'}
                </span>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Rate</span>
                <span className="font-medium text-foreground">{getPricingLabel()}</span>
              </div>

              {unit?.pricingModel === 'per_seat' && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Seats Allocated</span>
                  <span className="font-medium text-foreground">{seatsToAllocate} seats</span>
                </div>
              )}

              <div className="flex items-center justify-between text-sm pt-2 border-t border-border">
                <span className="text-muted-foreground font-semibold">Monthly Revenue</span>
                <span className="font-bold text-foreground text-lg">
                  {formatCurrency(calculateRevenue())}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-card border-t border-border p-4 md:p-6 flex gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            fullWidth
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            iconName="Check"
            fullWidth
          >
            Assign Tenant
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AssignTenantModal;